import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Tabs, SeparatorList } from "@narmi/design_system";
import Account from "byzantine/src/Account";
import Transaction from "byzantine/src/Transaction";
import type { TransactionResponse } from "byzantine/src/Transaction";
import type Pagination from "byzantine/src/Pagination";
import ApiPaginatedList from "byzantine/src/ApiPaginatedList";
import { convertFiltersToQuery } from "byzantine/src/TransactionSearchAndFilter";
import type { PossibleFilters } from "byzantine/src/TransactionSearchAndFilter";
import { useNotificationContext } from "cerulean";
import SectionCard from "../SectionCard";
import TransactionTable, { EmptyTransactionState } from "../TransactionTable";
import PageNavigation from "../page_navigation/PageNavigation";
import TransactionSearchAndFilter, {
  TransactionFilterTagSection,
} from "./TransactionSearchAndFilter";
import TransactionsDownloader from "./TransactionsDownloader";
import AdvancedExportLink from "./AdvancedExportLink";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import usePendingTransactions from "../hooks/usePendingTransactions";

interface HandleParseTransactionsProps {
  addSearchableCategories: (categories: string[]) => void;
  addSearchableTags: (tags: string[]) => void;
  transactions: Transaction[];
}

const HandleParseTransactions = ({
  addSearchableCategories,
  addSearchableTags,
  transactions,
}: HandleParseTransactionsProps) => {
  /* sole purpose of this component is to add tags and categories from newly-retrieved transactions
   * inside an effect hook since PaginatedTransactionTable cannot access the retrieved transactions and instead
   * passes that info to its children.
   */
  useEffect(() => {
    // only care about tags/categories from posted transactions since pending transactions cannot have tags/categories
    const postedTransactions = transactions.filter((t) => t?.posted_date);

    const tags = postedTransactions
      .map((transaction) => transaction.tags)
      .flat() // Transaction.tags is an array of strings so needs to be flattened first
      .filter((tag) => tag);

    const categories = postedTransactions
      .map((transaction) => transaction.category)
      .filter((category) => category);

    addSearchableTags(tags);
    addSearchableCategories(categories);
  }, [JSON.stringify(transactions)]);

  return null;
};

interface PaginatedTransactionTableProps {
  account: Account;
  searchFilters: PossibleFilters;
  addSearchableCategories: (categories: string[]) => void;
  addSearchableTags: (tags: string[]) => void;
  pendingTransactions: Transaction[];
  replyTime: string;
}

const PaginatedTransactionTable = ({
  account,
  searchFilters,
  addSearchableCategories,
  addSearchableTags,
  pendingTransactions,
  replyTime,
}: PaginatedTransactionTableProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const searchQuery = convertFiltersToQuery(searchFilters);

  const errorCallback = () => {
    sendNotification({
      type: "negative",
      text: l10n.getString("transactions-card-error-retrieving"),
    });
  };

  return (
    <ApiPaginatedList
      endpoint={`accounts/${account.id}/transactions`}
      queryParams={{ q: `${searchQuery} is:settled` }}
      keyname="transactions"
      pageSize={15}
      errorCallback={errorCallback}
    >
      {({
        loading: areTransactionsLoading,
        items,
        pagination,
      }: {
        loading: boolean;
        items: TransactionResponse[];
        pagination: Pagination;
      }) => {
        if (areTransactionsLoading) {
          return <SectionCard isLoading={areTransactionsLoading} />;
        }
        if (items.length === 0) {
          return (
            <div className="margin--top--s">
              {l10n.getString("transactions-card-no-transactions")}
            </div>
          );
        }
        // only show pending transactions for the first page of a retrieval without search query
        const filter =
          pagination.page !== 1 || searchQuery !== ""
            ? (t: Transaction) => t.posted_date
            : (t: Transaction) => t;
        const transactions = Transaction.sortByPostedDate(
          [
            ...pendingTransactions,
            ...items.map((a) => Transaction.deserialize(a, account)),
          ].filter(filter)
        );

        return (
          <div>
            <HandleParseTransactions
              addSearchableCategories={addSearchableCategories}
              addSearchableTags={addSearchableTags}
              transactions={transactions}
            />
            <TransactionTable
              isSourceMultipleAccounts={false}
              transactions={transactions}
              replyTime={replyTime}
            />
            <PageNavigation
              pagination={pagination}
              marginClasses="margin--top--l"
            />
          </div>
        );
      }}
    </ApiPaginatedList>
  );
};

interface TransactionCardProps {
  account: Account;
  recentTransactions: Transaction[];
  replyTime: string;
}

const TransactionsCard = ({
  account,
  recentTransactions,
  replyTime,
}: TransactionCardProps) => {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const features = useUserFeatures();
  const [searchFilters, setSearchFilters] = useState({});
  const [tabIndex, setTabIndex] = useState(0);
  const switchToAllTransactionsTab = () => setTabIndex(1);

  // using sets to ensure list of tags and categories are unique
  const [searchableTags, setSearchableTags] = useState<Set<string>>(new Set());
  const addSearchableTags = (newTags: string[]) => {
    setSearchableTags((prev) => new Set([...prev, ...newTags]));
  };
  const [searchableCategories, setSearchableCategories] = useState<Set<string>>(
    new Set()
  );
  const addSearchableCategories = (newCategories: string[]) => {
    setSearchableCategories((prev) => new Set([...prev, ...newCategories]));
  };
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>(
    []
  );
  usePendingTransactions({ accounts: [account], setPendingTransactions });

  const displayedRecentTransactions = pendingTransactions
    .concat(recentTransactions)
    .slice(0, 5);

  const RecentTransactionsTable = (
    <div className="padding--x--l padding--bottom--xs">
      <TransactionTable
        isSourceMultipleAccounts={false}
        transactions={displayedRecentTransactions}
        replyTime={replyTime}
      />
    </div>
  );

  const AllTransactionsTable = (
    <div className="padding--x--l padding--bottom--xs">
      <TransactionFilterTagSection
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
      />
      <PaginatedTransactionTable
        account={account}
        searchFilters={searchFilters}
        addSearchableTags={addSearchableTags}
        addSearchableCategories={addSearchableCategories}
        pendingTransactions={pendingTransactions}
        replyTime={replyTime}
      />
    </div>
  );

  return (
    <SectionCard isLoading={false} paddingSize={null}>
      {recentTransactions.length > 0 ? (
        <>
          <div className="padding--x--l padding--top--l padding--bottom--s">
            <SectionCard.Title
              text={l10n.getString("transactions-card-title")}
              button={
                <SeparatorList
                  items={[
                    <TransactionSearchAndFilter
                      key={l10n.getString(
                        "transactions-card-tab-search-filter"
                      )}
                      searchFilters={searchFilters}
                      setSearchFilters={setSearchFilters}
                      switchToAllTransactionsTab={switchToAllTransactionsTab}
                      searchableTags={[...searchableTags]}
                      searchableCategories={[...searchableCategories]}
                    />,
                    <TransactionsDownloader
                      key={l10n.getString("transactions-card-tab-download")}
                      accountUuid={account.id}
                    />,
                    ...(features.reports && currentUser?.isBusiness()
                      ? [<AdvancedExportLink key="advanced-export" />]
                      : []),
                  ]}
                />
              }
            />
          </div>
          <Tabs selectedIndex={tabIndex} onTabChange={setTabIndex}>
            <Tabs.List xPadding="l">
              <Tabs.Tab
                label={l10n.getString(
                  "transactions-tab-recent",
                  null,
                  "Recent"
                )}
                tabId="recent"
              />
              <Tabs.Tab
                label={l10n.getString("transactions-tab-all", null, "All")}
                tabId="all"
              />
            </Tabs.List>
          </Tabs>
          {tabIndex === 0 ? RecentTransactionsTable : null}
          {tabIndex === 1 ? AllTransactionsTable : null}
        </>
      ) : (
        <div className="padding--all--l">
          <SectionCard.Title text={l10n.getString("transactions-card-title")} />
          <EmptyTransactionState isSourceMultipleAccounts={false} />
        </div>
      )}
    </SectionCard>
  );
};
export default TransactionsCard;
